<script lang="ts">
  import { cn } from "$lib/utils";
  import { onDestroy, onMount } from "svelte";
  import Button from "$lib/components/ui/button/button.svelte";
  import { locationStore } from "$lib/stores/location.svelte";
  import { Info, Locate, Maximize, Minimize, Plus } from "lucide-svelte";
  import Minus from "@lucide/svelte/icons/minus";
  import { stateStore } from "$lib/stores/state.svelte";
  import type { Map as MaplibreMap } from "maplibre-gl";
  import PositionDialog from "../position-dialog.svelte";

  let {
    class: className,
    map = $bindable(),
  }: { class?: string; map?: MaplibreMap } = $props();

  async function handleLocationClick() {
    if (locationStore.isTrackingUser) {
      // If already tracking, center on user
      locationStore.centerMapOnUser();
    } else {
      // Start tracking
      const success = await locationStore.startTrackingUserLocation();
      if (success) {
        locationStore.centerMapOnUser();
      }
    }
  }

  async function openLocationDialog() {
    positionDialogOpen = true;
  }

  // Zoom functions for MapLibre
  function zoomIn() {
    locationStore.zoomIn();
  }

  function zoomOut() {
    locationStore.zoomOut();
  }

  function handleInfoClick() {
    stateStore.openAppInstall(
      "A térképről röviden",
      "Ezt a térképet a <b>beeco</b> magyar zöld startup készítette azzal a céllal, hogy megmutassa az emberek számára a közeli zöld helyeket. Fontos, hogy beszéljenek az emberek a zöldebb jövőről, de még fontosabb, hogy tegyünk is érte. Szerencsére sokkal több olyan hely van körülöttünk, ami segít csökkenteni ökológiai hatásunkat, mint gondolnánk. <br><br>Ez a térkép megmutatja a szerelőket, kölcsönzőket, szelektív hulladékgyűjtőket, szomjas fákat, bio gazdaságokat, bringás helyeket, használt cuccokat áruló vállalkozásokat és sok-sok mást is. <br><br>A térképnek elérhető mobil app verziója is, ahol kuponokat, játékos versenyt és zöld eseményeket is elérhetsz. Ha kíváncsi vagy töltsd le és próbáld ki vagy fedezd fel itt a környéked ökos oldalát!"
    );
  }

  onDestroy(() => {
    locationStore.clear();
  });

  let positionDialogOpen = $state(false);

  let isFullscreen = $state(false);

  async function toggleFullscreen() {
    const el = document.documentElement; // could also target a specific map container

    if (!document.fullscreenElement) {
      try {
        await el.requestFullscreen();
        isFullscreen = true;
      } catch (err) {
        console.error("Fullscreen error:", err);
      }
    } else {
      await document.exitFullscreen();
      isFullscreen = false;
    }
  }

  onMount(() => {
    document.addEventListener("fullscreenchange", () => {
      isFullscreen = !!document.fullscreenElement;
    });
  });
</script>

<div
  class={cn(
    "flex flex-col gap-2 opacity-75 hover:opacity-100 transition-opacity",
    className
  )}
>
  <!-- Zoom Controls -->
  <div class="flex flex-col gap-1 pb-8">
    <Button
      class="size-10 bg-white border border-black rounded-xs"
      onclick={handleInfoClick}
      title="Info"
    >
      <Info class="size-6 text-black" />
    </Button>
    <Button
      class="size-10 bg-white border border-black rounded-xs"
      onclick={zoomIn}
      title="Zoom in"
    >
      <Plus class="size-6 text-black" />
    </Button>
    <Button
      class="size-10 bg-white border border-black rounded-xs"
      onclick={zoomOut}
      title="Zoom out"
    >
      <Minus class="size-6 text-black" />
    </Button>
    <!-- Location Button with status indicator -->
    <Button
      class="size-10 bg-white border border-black rounded-xs"
      onclick={openLocationDialog}
      title={locationStore.isTrackingUser
        ? "Center on my location"
        : "Find my location"}
    >
      <Locate class="size-6 text-black" />
      {#if locationStore.isTrackingUser}
        <div
          class="absolute bottom-8 right-1 w-3 h-3 bg-secondary rounded-full animate-pulse"
        ></div>
      {/if}
    </Button>
    <Button
      class="size-10 bg-white border border-black rounded-xs"
      onclick={toggleFullscreen}
      title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
    >
      {#if isFullscreen}
        <Minimize class="size-6 text-black" />
      {:else}
        <Maximize class="size-6 text-black" />
      {/if}
    </Button>
  </div>
</div>

<PositionDialog
  onclick={handleLocationClick}
  bind:positionDialog={positionDialogOpen}
/>
